import { Truck } from "lucide-react";

import type { CartTotals } from "@/domain/cart/cart-item";
import { formatCatalogMoney } from "@/lib/catalog/money";
import { intercityShippingAmountBob } from "@/services/pricing/shipping-service";

export interface CartSummaryProps {
  readonly totals: CartTotals;
}

export function CartSummary({ totals }: Readonly<CartSummaryProps>) {
  if (!totals.currencyCode) {
    return null;
  }

  return (
    <section className="rounded-xl border border-outline-variant bg-surface p-4 text-sm shadow-sm">
      <h2 className="text-xl font-extrabold text-primary">Resumen del pedido</h2>
      <div className="mt-2">
        <SummaryRow
          label={`Subtotal (${totals.itemCount} productos)`}
          value={formatCatalogMoney(totals.subtotalRetail, totals.currencyCode)}
        />
        {totals.isWholesaleOrder ? (
          <p className="mt-1 text-xs font-bold text-secondary">Precios por mayor aplicados</p>
        ) : null}
        {totals.wholesaleDiscount > 0 ? (
          <SummaryRow
            label="Descuento mayorista"
            value={`-${formatCatalogMoney(totals.wholesaleDiscount, totals.currencyCode)}`}
          />
        ) : null}
        <SummaryRow
          label="Envío"
          value={
            totals.shippingAmount === 0
              ? "Gratis"
              : formatCatalogMoney(totals.shippingAmount, totals.currencyCode)
          }
        />
        <div className="mt-3 flex items-center justify-between border-t border-surface-container-high pt-3">
          <span className="font-extrabold text-foreground">Total estimado</span>
          <span className="text-2xl font-extrabold text-secondary">
            {formatCatalogMoney(totals.total, totals.currencyCode)}
          </span>
        </div>
      </div>
      {/* The intercity fee keeps a single source of truth: it is read from the
          shipping service, exactly like the destination field did before the
          explanation moved into the summary. */}
      <div className="mt-3 flex items-center gap-2.5 rounded-xl bg-secondary/10 p-3.5">
        <Truck aria-hidden="true" className="h-6 w-6 shrink-0 text-secondary" />
        <div className="text-sm leading-5">
          <p className="font-bold text-secondary">Envío gratis en La Paz y El Alto.</p>
          <p className="text-secondary">Al resto del país, Bs {intercityShippingAmountBob}.</p>
        </div>
      </div>
    </section>
  );
}

interface SummaryRowProps {
  readonly label: string;
  readonly value: string;
}

function SummaryRow({ label, value }: Readonly<SummaryRowProps>) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-muted">{label}</span>
      <span className="font-bold text-foreground">{value}</span>
    </div>
  );
}
